import type { ReactNode, JSX } from "react";

import type { ListRowProps } from "react-virtualized";
import { List, InfiniteLoader, AutoSizer } from "react-virtualized";

import type { ProWebsiteFeaturesLobsDtoLobSegmentData } from "@similarweb/pro-api-client";
import { ListItemPlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Box } from "@similarweb/ui-components/foundations";

import { DEFAULT_SELECTION_TYPE_PROPS } from "pro-features/components/AutocompleteWithTabs/const";
import { getDisabledState } from "pro-features/components/AutocompleteWithTabs/handlers";
import type { ISelectionTypeProps, SelectedItemsView } from "pro-features/components/AutocompleteWithTabs/types";
import { translate } from "app/filters/i18nFilter";
import { createLinesOfBusinessSegmentId } from "app/services/segments/LinesOfBusinessUtils";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";

import { useLinesOfBusinessQuery } from "../../hooks/useLinesOfBusinessQuery";
import { DropdownEmptyContent } from "../../SegmentsQueryBarAutocomplete";

import { LobListItem } from "./LobListItem";

const DEFAULT_ROW_HEIGHT = 42;
const DEFAULT_BOX_HEIGHT = 265;

interface ILobListItemsProps {
    onClick: (item: ProWebsiteFeaturesLobsDtoLobSegmentData) => void;
    applySelectionComponent?: ReactNode;
    query?: string;
    rowHeight?: number;
    boxHeight?: number;
    excludeItems?: string[];
    excludedDomains?: string[];
    selectionTypeProps?: ISelectionTypeProps;
    selectedItemsView?: SelectedItemsView;
    isDisabledListItemSupported?: boolean;
}

const LobListItems = ({
    onClick,
    selectedItemsView,
    applySelectionComponent,
    query = "",
    rowHeight = DEFAULT_ROW_HEIGHT,
    boxHeight = DEFAULT_BOX_HEIGHT,
    excludeItems = [],
    excludedDomains = [],
    isDisabledListItemSupported,
    selectionTypeProps = DEFAULT_SELECTION_TYPE_PROPS,
}: ILobListItemsProps) => {
    const isMultiSelect = Boolean(applySelectionComponent);

    const { data: linesOfBusinessData, isLoading, isError } = useLinesOfBusinessQuery(query);

    const items = linesOfBusinessData?.Data || [];
    const filteredData = items.filter((item) => {
        const lobId = createLinesOfBusinessSegmentId(item.Site, item.Lob);
        // Exclude if the LOB is already selected OR if its domain is selected as a website
        return !excludeItems.includes(lobId) && !excludedDomains.includes(item.Site);
    });

    const rowCount = filteredData.length;
    const listHeight = Math.min(rowCount * rowHeight, boxHeight);

    if (isLoading) {
        return (
            <Flex
                flexDirection="column"
                data-automation="lobListItems"
            >
                <Flex
                    gap="4px"
                    flexDirection="column"
                    padding="8px 14px"
                    height={`${boxHeight}px`}
                >
                    {Array.from({ length: 7 }, (_, index) => (
                        <ListItemPlaceholderLoader
                            key={index}
                            id={`loader-${index}`}
                        />
                    ))}
                </Flex>
            </Flex>
        );
    }

    if (rowCount === 0 || isError) {
        return (
            <Flex
                flexDirection="column"
                data-automation="lobListItems"
            >
                <DropdownEmptyContent message={translate("segments.querybar.dropdown.lob.noResults")} />
            </Flex>
        );
    }

    const rowRenderer = ({ key, index, style }: ListRowProps): JSX.Element => {
        const item = filteredData[index];
        const lobId = createLinesOfBusinessSegmentId(item.Site, item.Lob);
        const isDisabledItem = excludeItems.includes(lobId) || excludedDomains.includes(item.Site);

        const { isDisabled, isChecked, disabledTooltip } = isMultiSelect
            ? getDisabledState({
                  item: { id: lobId, type: EAssetType.Segment, isDisabled: isDisabledItem },
                  selectedItemsView,
                  selectionTypeProps,
              })
            : { isDisabled: false, isChecked: false, disabledTooltip: "" };

        const listItem = (
            <div style={style}>
                <LobListItem
                    data={item}
                    onClick={onClick}
                    isChecked={isChecked}
                    isMultiSelect={isMultiSelect}
                    isDisabled={Boolean(isDisabled && isDisabledListItemSupported)}
                />
            </div>
        );

        return isMultiSelect && isDisabled ? (
            <Tooltip
                placement="top-start"
                content={disabledTooltip}
                enabled={isDisabled}
                key={key}
            >
                {listItem}
            </Tooltip>
        ) : (
            <div key={key}>{listItem}</div>
        );
    };

    return (
        <Flex
            flexDirection="column"
            data-automation="lobListItems"
        >
            <Box
                height={`${listHeight}px`}
                width="100%"
            >
                <InfiniteLoader
                    isRowLoaded={({ index }) => !!filteredData[index]}
                    loadMoreRows={() => Promise.resolve()}
                    rowCount={rowCount}
                >
                    {({ onRowsRendered, registerChild }) => (
                        <AutoSizer disableHeight>
                            {({ width }) => (
                                <List
                                    height={listHeight}
                                    rowCount={rowCount}
                                    rowHeight={rowHeight}
                                    rowRenderer={rowRenderer}
                                    width={width}
                                    onRowsRendered={onRowsRendered}
                                    ref={registerChild}
                                />
                            )}
                        </AutoSizer>
                    )}
                </InfiniteLoader>
            </Box>
            {applySelectionComponent}
        </Flex>
    );
};

export default LobListItems;
